import { AppState } from "@src/store/reducers";
import i18next from "i18next";
import React from "react";
import { Link } from "react-router-dom";
import CartItemEditor from "../cart-item-editor/CartItemEditor";

import { getCartItemVersion, getFilmCostCalculation, getSubtotal, getVenueNames, getVenueType, PriceOptions } from "@src/app/films/film-utils";
import Image from '@src/components/image/Image';
import { CartItem, ReducerCartItem } from "@src/definitions/cart";
import { toggleSideBarCollapse } from "@src/store/app/app-actions";
import { editCartItem, removeCartItem, stopEditingCartItem, updateCartItem } from "@src/store/cart/cart-actions";
import { getDate, getDateRange, getLiveDate } from "@src/utils/dates";
import { CART_ITEM_FILM_TYPES } from "@src/utils/defaults";

interface Props {
    item: ReducerCartItem;
    cinemas: AppState["cinemas"]["data"];
    editCartItem: typeof editCartItem;
    removeCartItem: typeof removeCartItem;
    updateCartItem: typeof updateCartItem;
    stopEditingCartItem: typeof stopEditingCartItem;
    toggleSideBarCollapse: typeof toggleSideBarCollapse;
}

const CartItem = (props: Props) => {
    const { item, cinemas } = props;
    const { versions, versionId, startDate, endDate, encoreDate, encoreType} = item;

    const version             = getCartItemVersion(versions, versionId);
    const price               = getSubtotal({ ...item, cinemas });
    const filmCostCalculation = getFilmCostCalculation({ ...item, cinemas } as PriceOptions);
    const venueType           = getVenueType(version, item.bookingPeriodType);
    const venueNames          = getVenueNames(venueType, props.cinemas, item.cinemaIds, item.auditoriumIds);
    const isEditing           = item.isBeingEdited ? "is-editing" : "";
    const removeCartItem      = () => props.removeCartItem(item._id);
    const versionName         = version ? version.name : "";

    const statusMessage  = item.status === "pending"
        ? i18next.t("cart::Warning: Awaiting distributor's permission. Do not remove item from cart, or the request will be withdrawn.")
        : item.status;

    const editor = isEditing
        ? <CartItemEditor
                cartItem={item}
                isInCart={true}
                updateItem={props.updateCartItem}
                stopEditing={props.stopEditingCartItem}
            />
        : null;

    let dates;
    let typeText = "";

    if (CART_ITEM_FILM_TYPES.includes(item.filmType)) {
        switch(item.filmType) {
            case "live-event":
                dates = getLiveDate({ startDate, endDate });
                typeText = " Live event";
                break;
            case "film":
                dates = getDateRange(startDate, endDate);
                typeText = " Film DCP";
                break;
            case "encore":
                if(encoreType === "dcp") {
                    dates = getDateRange(startDate, endDate);
                    typeText = " DCP Encore";
                }

                if (encoreType === "mediabox") {
                    dates =   getDate(encoreDate, true, false);
                    typeText = " Mediabox Encore";
                }
                break;
            default:
                break;
        }
    }

    return (
        <article className={`CartItem ${isEditing}`}>
            <div className="CartItem__content">
                <div className="CartItem__left">
                    <Link to={`/film/${item.handle}`} className="CartItem__poster"
                        onClick={() => props.toggleSideBarCollapse(true)}>
                        <Image mediaItem={item.poster} width={150} />
                    </Link>
                </div>
                <div className="CartItem__right">
                    <h3 className="CartItem__title">
                        <b>{item.title}</b>
                    </h3>
                    {item.needsPermission &&
                        <p className={`CartItem__status CartItem__status--${item.status}`}>
                            {statusMessage}
                        </p>
                    }
                    <p className="CartItem__version">{versionName}</p>
                    <p className="CartItem__auditorium">{venueNames}</p>
                    <p className="CartItem__type">{typeText}</p>
                    <p className="CartItem__date">{dates}</p>
                    <p className="CartItem__price">
                        {price}
                    </p>
                    <p className="CartItem__calculation">
                        {i18next.t("glossary::Calculated from")}: {filmCostCalculation}
                    </p>
                    <div className="CartItem__actions">
                        <button className="CartItem__remove"
                            onClick={removeCartItem}>
                            {i18next.t("glossary::Remove")}
                        </button>
                    </div>
                </div>
            </div>
            {editor}
        </article>
    );
};

export default CartItem;
